import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { indexedDBStorage, IndexedDBStorage } from "./IndexedDBStorage";
import { logger } from "../utils/loggingUtils";

// Types from AIChatWidget
interface Message {
  id?: string; // Unique ID for deduplication
  text: string;
  type: "bot" | "user";
  timestamp: Date;
  threadId?: string | null;
}

// Storage provider interface
interface StorageProviderInterface {
  saveMessages: (threadId: string | null, messages: Message[]) => Promise<void>;
  loadMessages: (threadId: string | null) => Promise<Message[]>;
  getThreadId: () => Promise<string | null>;
  saveThreadId: (threadId: string | null) => Promise<void>;
  clearMessages: (threadId?: string | null) => Promise<void>;
  hasExistingConversation: () => Promise<boolean>;
  isIndexedDBSupported: boolean;
  isInitialized: boolean;
  verifyDataExists: () => Promise<void>;
}

// Create context with default no-op values
const StorageContext = createContext<StorageProviderInterface>({
  saveMessages: async () => {
    logger.warn("StorageContext: Default saveMessages called - context not initialized");
  },
  loadMessages: async () => {
    logger.warn("StorageContext: Default loadMessages called - context not initialized");
    return [];
  },
  getThreadId: async () => {
    logger.warn("StorageContext: Default getThreadId called - context not initialized");
    return null;
  },
  saveThreadId: async () => {
    logger.warn("StorageContext: Default saveThreadId called - context not initialized");
  },
  clearMessages: async () => {
    logger.warn("StorageContext: Default clearMessages called - context not initialized");
  },
  hasExistingConversation: async () => {
    logger.warn("StorageContext: Default hasExistingConversation called - context not initialized");
    return false;
  },
  isIndexedDBSupported: false,
  isInitialized: false,
  verifyDataExists: async () => {
    logger.warn("StorageContext: Default verifyDataExists called - context not initialized");
  },
});

// IndexedDB provider wrapper
class IndexedDBProvider implements StorageProviderInterface {
  private storage: IndexedDBStorage;
  readonly isIndexedDBSupported: boolean = true;
  isInitialized: boolean = false;

  constructor(storage: IndexedDBStorage) {
    this.storage = storage;

    // Bind methods to ensure proper this context
    this.saveMessages = this.saveMessages.bind(this);
    this.loadMessages = this.loadMessages.bind(this);
    this.getThreadId = this.getThreadId.bind(this);
    this.saveThreadId = this.saveThreadId.bind(this);
    this.clearMessages = this.clearMessages.bind(this);
    this.hasExistingConversation = this.hasExistingConversation.bind(this);
    this.verifyDataExists = this.verifyDataExists.bind(this);
    this.checkInitialization = this.checkInitialization.bind(this);

    // Initialize isInitialized property
    this.checkInitialization();

    logger.log("IndexedDBProvider: Provider constructed with storage instance");
  }

  // Method to check the initialization status of the storage
  checkInitialization(): boolean {
    this.isInitialized = this.storage.isInitialized();
    return this.isInitialized;
  }

  /**
   * Save messages to IndexedDB
   */
  async saveMessages(threadId: string | null, messages: Message[]): Promise<void> {
    if (!threadId || messages.length === 0) {
      logger.log("IndexedDBProvider: No threadId or empty messages array, skipping save");
      return;
    }

    logger.log(`IndexedDBProvider: Saving ${messages.length} messages with threadId=${threadId}`);

    try {
      // Extra verification before trying to save
      if (!this.isInitialized || !this.storage.isInitialized()) {
        logger.error("IndexedDBProvider: Storage not initialized before saveMessages call");
        throw new Error("Storage not initialized");
      }

      await this.storage.saveMessages(threadId, messages);
      logger.log("IndexedDBProvider: Messages saved successfully");
    } catch (error) {
      logger.error("IndexedDBProvider: Error saving messages:", error);
      throw error;
    }
  }

  /**
   * Load messages from IndexedDB
   */
  async loadMessages(threadId: string | null): Promise<Message[]> {
    if (!threadId) {
      logger.log("IndexedDBProvider: No threadId provided, returning empty array");
      return [];
    }

    logger.log(`IndexedDBProvider: Loading messages for threadId=${threadId}`);

    try {
      const messages = await this.storage.loadMessages(threadId);
      logger.log(`IndexedDBProvider: Loaded ${messages.length} messages`);
      return messages;
    } catch (error) {
      logger.error("IndexedDBProvider: Error loading messages:", error);
      return [];
    }
  }

  /**
   * Get the latest thread ID from IndexedDB
   */
  async getThreadId(): Promise<string | null> {
    logger.log("IndexedDBProvider: Getting thread ID");

    try {
      const threadId = await this.storage.getThreadId();
      logger.log(`IndexedDBProvider: Got threadId=${threadId}`);
      return threadId;
    } catch (error) {
      logger.error("IndexedDBProvider: Error getting thread ID:", error);
      return null;
    }
  }

  /**
   * Save thread ID to IndexedDB
   */
  async saveThreadId(threadId: string | null): Promise<void> {
    if (!threadId) {
      logger.log("IndexedDBProvider: No threadId provided, skipping save");
      return;
    }

    logger.log(`IndexedDBProvider: Saving threadId=${threadId}`);

    try {
      await this.storage.saveThreadId(threadId);
      logger.log("IndexedDBProvider: Thread ID saved successfully");
    } catch (error) {
      logger.error("IndexedDBProvider: Error saving thread ID:", error);
      throw error;
    }
  }

  /**
   * Clear messages from IndexedDB
   */
  async clearMessages(threadId?: string | null): Promise<void> {
    logger.log(`IndexedDBProvider: Clearing messages, threadId=${threadId || "all"}`);

    try {
      await this.storage.clearMessages(threadId);
      logger.log("IndexedDBProvider: Messages cleared successfully");
    } catch (error) {
      logger.error("IndexedDBProvider: Error clearing messages:", error);
      throw error;
    }
  }

  /**
   * Check if there's an existing conversation in IndexedDB
   */
  async hasExistingConversation(): Promise<boolean> {
    logger.log("IndexedDBProvider: Checking for existing conversation");

    try {
      const hasConversation = await this.storage.hasExistingConversation();
      logger.log(`IndexedDBProvider: Has existing conversation: ${hasConversation}`);
      return hasConversation;
    } catch (error) {
      logger.error("IndexedDBProvider: Error checking for existing conversation:", error);
      return false;
    }
  }

  /**
   * Verify data exists in database
   */
  async verifyDataExists(): Promise<void> {
    logger.log("IndexedDBProvider: Verifying data exists");

    try {
      await this.storage.verifyDataExists();
      logger.log("IndexedDBProvider: Data verification completed");
    } catch (error) {
      logger.error("IndexedDBProvider: Error verifying data:", error);
    }
  }
}

interface StorageProviderProps {
  children: ReactNode;
}

// Component that provides storage context
export const StorageProvider: React.FC<StorageProviderProps> = ({ children }) => {
  const [provider, setProvider] = useState<StorageProviderInterface | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isIndexedDBSupported, setIsIndexedDBSupported] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeStorage = async () => {
      logger.log("StorageContext: Initializing storage provider...");

      try {
        // Try to initialize IndexedDB storage
        logger.log("StorageContext: Creating IndexedDB provider instance");
        const indexedDBProvider = new IndexedDBProvider(indexedDBStorage);

        // Initialize the storage
        try {
          await indexedDBStorage.init();
          logger.log("StorageContext: IndexedDB storage initialized successfully");

          if (!isMounted) return;

          // Check initialization status and update it explicitly
          const isInitialized = indexedDBStorage.isInitialized();
          logger.log("StorageContext: Raw IndexedDB initialization status:", isInitialized);
          indexedDBProvider.isInitialized = isInitialized;

          logger.log(
            "StorageContext: IndexedDB provider initialization status:",
            indexedDBProvider.isInitialized,
          );

          // At this point, we've already run a test write in the init() function
          // but we can verify the db is still accessible
          try {
            await indexedDBStorage.verifyDataExists();
            logger.log("StorageContext: Successfully verified database accessibility");
          } catch (verifyError) {
            logger.error("StorageContext: Database verification failed:", verifyError);
            // We can still proceed since the init() succeeded
          }

          // Store provider and update state
          setProvider(indexedDBProvider);
          setIsIndexedDBSupported(true);
          setIsInitialized(indexedDBProvider.isInitialized);
          logger.log("StorageContext: Using IndexedDB for storage");
        } catch (error) {
          logger.error("StorageContext: Failed to initialize IndexedDB storage:", error);
          if (!isMounted) return;

          // Even though init failed, provide the instance so the widget can show proper errors
          indexedDBProvider.isInitialized = false;
          setProvider(indexedDBProvider);
          setIsIndexedDBSupported(false);
          setIsInitialized(false);
          logger.warn("StorageContext: Using uninitialized IndexedDB provider");
        }
      } catch (error) {
        logger.error("StorageContext: Error initializing storage provider:", error);
        if (!isMounted) return;

        // Provide a basic provider that will fail gracefully
        setProvider({
          saveMessages: async () => {
            logger.error("Storage not available");
            throw new Error("Storage not available");
          },
          loadMessages: async () => [],
          getThreadId: async () => null,
          saveThreadId: async () => {
            logger.error("Storage not available");
            throw new Error("Storage not available");
          },
          clearMessages: async () => {
            logger.error("Storage not available");
            throw new Error("Storage not available");
          },
          hasExistingConversation: async () => false,
          isIndexedDBSupported: false,
          isInitialized: false,
          verifyDataExists: async () => {
            logger.error("Storage not available");
            throw new Error("Storage not available");
          },
        });
        setIsIndexedDBSupported(false);
        setIsInitialized(false);
      }
    };

    initializeStorage();

    return () => {
      isMounted = false;
    };
  }, []);

  // In the value object, merge the provider with extra state
  const contextValue: StorageProviderInterface = provider
    ? {
        ...provider,
        isIndexedDBSupported,
        isInitialized,
      }
    : {
        saveMessages: async () => {
          logger.warn("StorageContext: Provider not initialized");
          throw new Error("Storage provider not initialized");
        },
        loadMessages: async () => {
          logger.warn("StorageContext: Provider not initialized");
          return [];
        },
        getThreadId: async () => {
          logger.warn("StorageContext: Provider not initialized");
          return null;
        },
        saveThreadId: async () => {
          logger.warn("StorageContext: Provider not initialized");
          throw new Error("Storage provider not initialized");
        },
        clearMessages: async () => {
          logger.warn("StorageContext: Provider not initialized");
          throw new Error("Storage provider not initialized");
        },
        hasExistingConversation: async () => false,
        isIndexedDBSupported: false,
        isInitialized: false,
        verifyDataExists: async () => {
          logger.warn("StorageContext: Provider not initialized");
          throw new Error("Storage provider not initialized");
        },
      };

  return <StorageContext.Provider value={contextValue}>{children}</StorageContext.Provider>;
};

// Hook to use the storage context
export const useStorage = (): StorageProviderInterface => {
  return useContext(StorageContext);
};

export default StorageProvider;
